<template>
  <div class="budgets-tab">
    <div class="budgets-tab__filters">
      <b-form-select
        v-if="hasDivisions"
        v-model="selectedDivision"
        :options="divisionOptions"
        class="budgets-tab__filter"
      />
      <b-form-select v-model="selectedType" :options="typeOptions" class="budgets-tab__filter" />
      <b-form-select v-model="selectedStatus" :options="statusOptions" class="budgets-tab__filter" />
      <b-button v-if="hasActiveFilters" variant="link" class="budgets-tab__reset" @click="resetFilters">
        <font-awesome-icon :icon="['fal', 'times']" class="mr-1" />
        Reset filters
      </b-button>
      <div v-if="!readOnly" class="budgets-tab__actions">
        <b-button v-if="awardsAvailable.length" variant="outline-primary" @click="awardBudgetModalRef?.show()">
          <font-awesome-icon :icon="['fal', 'plus']" class="mr-1" />
          Add award budget
        </b-button>
        <b-button variant="outline-primary" @click="potModalRef?.show()">
          <font-awesome-icon :icon="['fal', 'plus']" class="mr-1" />
          Add manager budget
        </b-button>
        <b-button variant="link" class="budgets-tab__settings-btn" title="Budget settings" @click="onOpenSettings">
          <font-awesome-icon :icon="['fal', 'cog']" />
        </b-button>
      </div>
    </div>

    <app-loading :loading="isLoading" />
    <template v-if="!isLoading">
      <sme-alert v-if="alert" level="warning">{{ budgetAlertMessage(alert) }}</sme-alert>

      <b-table
        v-if="visibleBudgets.length > 0"
        :items="visibleBudgets"
        :fields="fields"
        :tbody-tr-class="rowClass"
        hover
        responsive
        borderless
        class="budgets-tab__table"
      >
        <template #cell(name)="{ item }">
          <div class="budgets-tab__name-group">
            <span class="budgets-tab__name">{{ budgetName(item) }}</span>
            <span v-if="item.kind === BUDGET_KINDS.POT" class="budgets-tab__subtitle">{{ item.name }}</span>
          </div>
        </template>
        <template #cell(kind)="{ item }">
          <b-badge pill variant="light" class="budgets-tab__type">{{ BUDGET_TYPE_LABELS[item.kind] }}</b-badge>
        </template>
        <template #cell(budget_period)="{ item }">{{ formatBudgetPeriod(item.budget_period) }}</template>
        <template #cell(period_end_at)="{ item }">{{ formatDate(item.period_end_at) }}</template>
        <template #cell(amount_minor)="{ item }">{{ formatAmount(item.amount_minor) }}</template>
        <template #cell(spent_minor)="{ item }">{{ formatAmount(item.spent_minor) }}</template>
        <template #cell(remaining_minor)="{ item }">{{ formatAmount(item.remaining_minor) }}</template>
        <template #cell(usage)="{ item }">
          <b-badge v-if="!item.is_active" pill variant="secondary">
            {{ isAwardDisabled(item) ? 'Award disabled' : 'Disabled' }}
          </b-badge>
          <budget-usage-bar v-else :budget="item" compact />
        </template>
        <template #cell(actions)="{ item }">
          <b-button v-if="!readOnly" variant="link" size="sm" class="p-0" @click="edit(item)">Edit</b-button>
        </template>
      </b-table>
      <p v-else class="budgets-tab__empty">
        {{ allBudgets.length ? 'No budgets match these filters.' : 'No budgets yet.' }}
      </p>
    </template>

    <award-budget-modal
      v-if="!readOnly"
      ref="awardBudgetModalRef"
      :available-awards="awardsAvailable"
      @saved="$emit('changed')"
    />
    <pot-modal v-if="!readOnly" ref="potModalRef" @saved="$emit('changed')" />

    <!-- lazy: remount BudgetSettings on each open so it re-reads the latest
         persisted setting from company state (see BudgetSettings.vue). -->
    <b-modal v-if="!readOnly" ref="settingsModalRef" title="Budget settings" size="lg" hide-footer centered lazy>
      <budget-settings @saved="settingsModalRef?.hide()" />
    </b-modal>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import AppLoading from '@/components/AppLoading.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import AwardBudgetModal from '@/pages/recognition/components/budgets/AwardBudgetModal.vue';
import BudgetSettings from '@/pages/recognition/components/budgets/BudgetSettings.vue';
import BudgetUsageBar from '@/pages/recognition/components/budgets/BudgetUsageBar.vue';
import PotModal from '@/pages/recognition/components/budgets/PotModal.vue';
import { getRecognitionBudgets } from '@/queries/recognition';
import {
  BUDGET_KINDS,
  BUDGET_STATUS_FILTERS,
  BUDGET_TYPE_LABELS,
  awardsWithoutBudget,
  budgetAlert,
  budgetAlertMessage,
  filterBudgets,
  formatBudgetPeriod,
  isAwardDisabled,
  legacyAwardBudgets,
  sortByUsage,
} from '@/pages/recognition/utils/budgets';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { toCurrency } from '@/utils';
import { toFormattedDate } from '@/utils/date';
import State from '@/state/State';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['changed']);

const awardBudgetModalRef = ref(null);
const potModalRef = ref(null);
const settingsModalRef = ref(null);
const onOpenSettings = () => settingsModalRef.value?.show();

const { data: budgets, isLoading } = useQuery(getRecognitionBudgets({ includeInactive: true }));
const { divisions, hasDivisions } = useDivisionSelection();

const selectedDivision = ref(null);
const selectedType = ref(null);
const selectedStatus = ref(BUDGET_STATUS_FILTERS.ACTIVE);

const divisionOptions = computed(() => [
  { value: null, text: 'Entire company' },
  ...divisions.value.map(division => ({ value: division.division_id, text: division.name })),
]);
const typeOptions = [
  { value: null, text: 'All budget types' },
  { value: BUDGET_KINDS.AWARD, text: 'Award budgets' },
  { value: BUDGET_KINDS.POT, text: 'Manager budgets' },
];
const statusOptions = [
  { value: BUDGET_STATUS_FILTERS.ACTIVE, text: 'Active' },
  { value: BUDGET_STATUS_FILTERS.LOW, text: 'Running low' },
  { value: BUDGET_STATUS_FILTERS.EXHAUSTED, text: 'Exhausted' },
  { value: BUDGET_STATUS_FILTERS.DISABLED, text: 'Disabled' },
  { value: null, text: 'All statuses' },
];

const hasActiveFilters = computed(
  () => Boolean(selectedDivision.value || selectedType.value) || selectedStatus.value !== BUDGET_STATUS_FILTERS.ACTIVE,
);
const resetFilters = () => {
  selectedDivision.value = null;
  selectedType.value = null;
  selectedStatus.value = BUDGET_STATUS_FILTERS.ACTIVE;
};

const allBudgets = computed(() => [...(budgets.value || []), ...legacyAwardBudgets(props.programs)]);
const visibleBudgets = computed(() =>
  sortByUsage(
    filterBudgets(allBudgets.value, {
      type: selectedType.value,
      status: selectedStatus.value,
      divisionId: selectedDivision.value,
    }),
  ),
);
const alert = computed(() => budgetAlert(allBudgets.value));
const awardsAvailable = computed(() => awardsWithoutBudget(props.programs));

const currency = computed(() => State.state?.company?.default_currency);
const formatAmount = minor => toCurrency(minor ?? 0, currency.value).replace(/\.00$/, '');

const budgetName = budget =>
  (budget.kind === BUDGET_KINDS.POT ? budget.holder?.full_name : budget.award?.program_name) || budget.name;
const formatDate = date => (date ? toFormattedDate(date, 'D MMM YYYY') : '');
const rowClass = item => (item && !item.is_active ? 'budgets-tab__row--disabled' : '');

const edit = budget => (budget.kind === BUDGET_KINDS.POT ? potModalRef.value : awardBudgetModalRef.value)?.show(budget);

const fields = [
  { key: 'name', label: 'Name' },
  { key: 'kind', label: 'Type' },
  { key: 'budget_period', label: 'Period' },
  { key: 'period_end_at', label: 'Renews' },
  { key: 'amount_minor', label: 'Budget' },
  { key: 'spent_minor', label: 'Spent' },
  { key: 'remaining_minor', label: 'Remaining' },
  { key: 'usage', label: 'Usage' },
  { key: 'actions', label: '', tdClass: 'text-right' },
];
</script>

<style scoped lang="scss">
.budgets-tab {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: var(--palette-color-default-lighten-95);
    border-radius: 0.5rem;
  }

  &__filter {
    width: auto;
    min-width: 170px;
    border-color: var(--palette-color-default-lighten-80);
    padding: 0.5rem 2.25rem 0.5rem 1rem;
    text-overflow: ellipsis;

    &:focus {
      border-color: var(--palette-color-primary);
      box-shadow: none;
    }
  }

  &__reset {
    font-size: 0.875rem;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
  }

  &__settings-btn {
    color: var(--palette-color-default);
    text-decoration: none;
    padding: 0.5rem 0.75rem;
    font-size: 1.125rem;
    background-color: transparent;
    border-color: transparent;

    &:hover {
      color: var(--palette-color-default-lighten-20);
      text-decoration: none;
      background-color: transparent;
      border-color: transparent;
    }
  }

  &__type {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 500;
  }

  &__table :deep(.budgets-tab__row--disabled) {
    opacity: 0.5;
  }

  &__table {
    margin-bottom: 0;

    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
      white-space: nowrap;
    }

    :deep(tbody td) {
      padding: 1rem 0.75rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      white-space: nowrap;
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__name-group {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  &__name {
    font-weight: 500;
  }

  &__subtitle {
    font-size: 0.75rem;
    color: var(--palette-color-default-lighten-30);
  }

  &__empty {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);
    padding: 1.5rem;
    text-align: center;
    border: 1px dashed var(--palette-color-default-lighten-90);
    border-radius: 0.5rem;
    margin: 0;
  }
}
</style>
